import { useContext, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import NotesIcon from "@mui/icons-material/Notes";

import { QRCodeCanvas } from "qrcode.react";

import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import Loader from "../component/loading-screen/Loader";
import { okSuccessToast, apiErrorToast } from "../utils/ToastUtil";
import AuthContext from "../store/AuthContext";

const DynamicQr = () => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    customerName: "",
    customerMobile: "",
    amount: "",
    remark: "",
  });

  const [qrCode, setQrCode] = useState("");
  const [qrData, setQrData] = useState(null);
  const [errors, setErrors] = useState({});

  const authCtx = useContext(AuthContext);
  const location = authCtx.location;

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Customer Name
    if (name === "customerName") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    // Customer Mobile
    if (name === "customerMobile") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Amount
    if (name === "amount") {
      updatedValue = value.replace(/[^0-9.]/g, "");

      // Prevent multiple decimal points
      if ((updatedValue.match(/\./g) || []).length > 1) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    }

    if (!formData.customerMobile) {
      newErrors.customerMobile = "Customer mobile is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.customerMobile)) {
      newErrors.customerMobile = "Enter a valid 10 digit mobile number";
    }

    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else if (Number(formData.amount) <= 0) {
      newErrors.amount = "Enter a valid amount";
    }

    if (!formData.remark.trim()) {
      newErrors.remark = "Remark is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Clear old QR before generating new one
    setQrCode("");
    setQrData(null);

    const payload = {
      name: formData.customerName.trim(),
      customer_number: formData.customerMobile,
      amount: Number(formData.amount),
      remark: formData.remark.trim(),
      latitude: location.lat,
      longitude: location.long,
    };

    console.log("DYNAMIC QR PAYLOAD", payload);

    postJsonData(
      ApiEndpoints.DYNAMIC_QR,
      payload,
      setLoading,

      (res) => {
        console.log("DYNAMIC QR SUCCESS", res);

        okSuccessToast("QR generated successfully");

        /*
         * Backend response:
         *
         * res.data.data.qr_string
         *
         * OR
         *
         * res.data.data.qr_base64
         *
         * Both currently contain the UPI URI.
         */

        const qrString =
          res?.data?.data?.qr_string || res?.data?.data?.qr_base64 || "";

        if (qrString) {
          setQrCode(qrString);
          setQrData(res?.data?.data);
        }
      },

      (err) => {
        console.log("DYNAMIC QR ERROR", err);
        apiErrorToast(err);
      },
    );
  };

  return (
    <>
      <Loader loading={loading} />

      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Card
          elevation={4}
          sx={{
            width: "100%",
            maxWidth: 600,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                color="primary"
                gutterBottom
              >
                Dynamic QR Payment
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2.5}>
                {/* Customer Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    placeholder="Enter customer name"
                    error={Boolean(errors.customerName)}
                    helperText={errors.customerName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Customer Mobile */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Customer Mobile"
                    name="customerMobile"
                    value={formData.customerMobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    error={Boolean(errors.customerMobile)}
                    helperText={errors.customerMobile}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    error={Boolean(errors.amount)}
                    helperText={errors.amount}
                    inputProps={{
                      inputMode: "decimal",
                      min: 1,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Remark */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Remark"
                    name="remark"
                    value={formData.remark}
                    onChange={handleChange}
                    placeholder="Enter remark"
                    error={Boolean(errors.remark)}
                    helperText={errors.remark}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            alignSelf: "flex-start",
                            mt: 1.5,
                          }}
                        >
                          <NotesIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      mt: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: "16px",
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                  >
                    {loading ? "Processing..." : "Pay Now"}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* QR CODE */}
            {qrCode && (
              <Box
                sx={{
                  mt: 4,
                  textAlign: "center",
                  borderTop: "1px solid #eee",
                  pt: 3,
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Scan QR to Pay
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      display: "inline-flex",
                    }}
                  >
                    <QRCodeCanvas
                      value={qrCode}
                      size={280}
                      level="H"
                      includeMargin={true}
                    />
                  </Box>
                </Box>

                {/* Transaction Details */}
                {qrData && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      Order ID: <strong>{qrData.order_id}</strong>
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Amount: ₹<strong>{qrData.amount}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default DynamicQr;
